import { useContext, useEffect, useState } from "react";
import { Button, Select } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { hasValidSarPrecision } from "../../../../utils/billingMoney";
import {
  createInvoiceAPI,
  createStandaloneInvoiceAPI,
  issueInvoiceAPI,
  listBillingStoresAPI,
  listSubscriptionsAPI,
} from "../../../../utils/api/billing";
import { useBillingAdminText } from "../billingAdminI18n";

const emptyLine = () => ({ key: Math.random().toString(36).slice(2), descriptionEn: "", quantity: "1", unitPrice: "" });

// Canonical v141 issues one invoice from up to several independently
// toggleable line-item blocks (package/integration/device/custom). The real
// backend models a subscription-cycle charge and free-form service/device
// items as two separate primitives (createInvoiceAPI vs
// createStandaloneInvoiceAPI) rather than one generic line-item bag, so this
// modal offers both together: an optional existing-subscription link plus
// any number of custom line items — the backend recalculates and validates
// every amount regardless of what the form shows.
const IssueInvoiceModal = ({ onCancel, onIssued }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [stores, setStores] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listBillingStoresAPI(token)
      .then((result) => setStores(result.data || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const selectStore = async (nextStoreId) => {
    setStoreId(nextStoreId);
    setSubscriptionId("");
    setError("");
    if (!nextStoreId) {
      setSubscriptions([]);
      return;
    }
    try {
      const result = await listSubscriptionsAPI(token, { storeId: nextStoreId, status: "active", limit: 100 });
      setSubscriptions(result.data || []);
    } catch (err) {
      setSubscriptions([]);
      setError(err.message);
    }
  };

  const updateLine = (key, patch) => setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const addLine = () => setLines((current) => [...current, emptyLine()]);
  const removeLine = (key) => setLines((current) => current.filter((line) => line.key !== key));

  const validLines = lines.filter((line) => line.descriptionEn.trim() && line.unitPrice.trim());

  const submit = async () => {
    if (!can("billing.admin.invoice.issue")) return;
    if (saving) return;
    setError("");
    if (!storeId) {
      setError(text.invoices.filters.allRestaurants);
      return;
    }
    if (!subscriptionId && validLines.length === 0) {
      setError(text.invoices.issueModal.atLeastOneItem);
      return;
    }
    for (const line of validLines) {
      if (!hasValidSarPrecision(line.unitPrice)) {
        setError(text.plans.basePricePrecision);
        return;
      }
    }
    setSaving(true);
    try {
      const serviceItems = validLines.map((line) => ({
        descriptionEn: line.descriptionEn.trim(),
        descriptionAr: line.descriptionEn.trim(),
        quantity: Number(line.quantity) || 1,
        unitPrice: line.unitPrice.trim(),
      }));
      const draft = subscriptionId
        ? await createInvoiceAPI(token, {
            storeId,
            subscriptionId,
            serviceItems,
            idempotencyKey: `admin-issue-${Date.now()}-${Math.random().toString(10).slice(2)}`,
          })
        : await createStandaloneInvoiceAPI(token, {
            storeId,
            serviceItems,
            idempotencyKey: `admin-issue-${Date.now()}-${Math.random().toString(10).slice(2)}`,
          });
      await issueInvoiceAPI(token, draft.data._id);
      onIssued();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal">
        <h3>{text.invoices.issueModal.title}</h3>
        <p>{text.invoices.issueModal.description}</p>
        {error && <div className="billing-error">{error}</div>}
        {loading ? (
          <div className="billing-loading">{text.common.loading}</div>
        ) : (
          <div className="billing-plan-modal-form">
            <Select
              label={text.common.restaurant}
              value={storeId}
              options={stores.map((store) => ({ value: store.id, label: `${store.businessName} (${store.id})` }))}
              onChange={(event) => selectStore(event.target.value)}
              placeholder={text.invoices.selectRestaurant}
            />
            <Select
              label={text.invoices.issueModal.linkSubscription}
              value={subscriptionId}
              onChange={(event) => setSubscriptionId(event.target.value)}
              options={[
                { value: "", label: text.invoices.issueModal.noSubscription },
                ...subscriptions.map((subscription) => ({
                  value: subscription._id,
                  label: `${subscription.planSnapshot?.code} v${subscription.planSnapshot?.version} — ${subscription.type}`,
                })),
              ]}
              disabled={!storeId}
            />
            <div className="billing-form-grid-header">{text.invoices.issueModal.lineItems}</div>
            {lines.map((line) => (
              <div className="billing-line-item-row" key={line.key}>
                <input
                  className="billing-line-input"
                  value={line.descriptionEn}
                  onChange={(event) => updateLine(line.key, { descriptionEn: event.target.value })}
                  placeholder={text.invoices.issueModal.itemDescription}
                />
                <input
                  className="billing-line-input billing-line-input-narrow"
                  type="number"
                  min="1"
                  value={line.quantity}
                  onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
                  placeholder={text.invoices.issueModal.itemQuantity}
                />
                <input
                  className="billing-line-input billing-line-input-narrow"
                  value={line.unitPrice}
                  onChange={(event) => updateLine(line.key, { unitPrice: event.target.value })}
                  placeholder={text.invoices.issueModal.itemUnitPrice}
                />
                {lines.length > 1 && (
                  <Button size="sm" appearance="ghost" onClick={() => removeLine(line.key)}>
                    {text.invoices.issueModal.removeLineItem}
                  </Button>
                )}
              </div>
            ))}
            <Button size="sm" appearance="outline" onClick={addLine}>
              {text.invoices.issueModal.addLineItem}
            </Button>
            <div className="billing-modal-actions">
              <Button appearance="filled" loading={saving} onClick={submit}>
                {saving ? text.invoices.issueModal.issuing : text.invoices.issueModal.confirmIssue}
              </Button>
              <Button appearance="ghost" onClick={onCancel}>
                {text.common.cancel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueInvoiceModal;
